import { createFileRoute, redirect } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';

import { recoveryService } from '@/lib/auth/ory/service';
import {
  getErrorResponseData,
  is400Error,
  isFlowExpired,
  type OryKratosErrorResponse,
} from '@/lib/auth/ory/service/types';

const SearchSchema = z.object({
  flow: z.string().optional(),
});

export const Route = createFileRoute('/auth/password-reset/')({
  validateSearch: zodValidator(SearchSchema),
  loaderDeps: ({ search }) => ({ flow: search.flow }),
  loader: async ({ deps }) => {
    const flowId = deps.flow;

    // メール内リンク（?flow=...）で来た場合は既存フローを取得
    if (flowId) {
      try {
        const flow = await recoveryService.getFlow(flowId);
        return { flow, flowExpired: false };
      } catch (error) {
        // self_service_flow_expired (410): 新規フローを発行してバナー表示する（ユーザーに再入力を促す）
        if (isFlowExpired(error)) {
          try {
            const flow = await recoveryService.initFlow();
            return { flow, flowExpired: true };
          } catch (reinitError) {
            console.error(
              'Failed to re-init recovery flow after expiration:',
              reinitError,
            );
            throw redirect({ to: '/auth/password-reset' });
          }
        }

        // それ以外の不正/エラーは URL から flow を取り除いてやり直す
        console.error('Failed to get recovery flow:', error);
        throw redirect({ to: '/auth/password-reset' });
      }
    }

    // 通常アクセスは新規フロー作成
    try {
      const flow = await recoveryService.initFlow();
      return { flow, flowExpired: false };
    } catch (error) {
      // 既にサインイン済み（Kratos: session_already_available）の場合は、
      // password-reset ではなく通常の導線（アプリ）へ戻す。
      if (is400Error(error)) {
        const errorData = getErrorResponseData<OryKratosErrorResponse>(error);
        if (errorData?.error?.id === 'session_already_available') {
          throw redirect({ to: '/apps' });
        }
      }
      console.error('Failed to initialize recovery flow:', error);
      throw error;
    }
  },
});
